import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { Terminal, KeyRound, Fingerprint, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const ok = await login(username, password);
    setSubmitting(false);
    if (ok) {
      navigate("/", { replace: true });
    } else {
      setErr("Ungültige Zugangsdaten");
    }
  };

  const passkeyLogin = async () => {
    setErr("");
    if (!browserSupportsWebAuthn()) {
      setErr("Dein Browser unterstützt keine Passkeys / WebAuthn.");
      return;
    }
    setPasskeyBusy(true);
    try {
      const { data: opts } = await api.post("/auth/webauthn/login/begin", {
        username: username || undefined,
      });
      const sessionId = opts.session_id;
      delete opts.session_id;
      const assertion = await startAuthentication({ optionsJSON: opts });
      const { data } = await api.post("/auth/webauthn/login/complete", {
        session_id: sessionId,
        credential: assertion,
      });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      window.location.href = "/";
    } catch (e) {
      setErr(formatApiError(e) || "Passkey-Login abgebrochen");
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-screen grid lg:grid-cols-2 bg-[#030303]"
    >
      {/* LEFT — CODE TEXTURE */}
      <div
        className="hidden lg:flex relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(rgba(3,3,3,0.88), rgba(3,3,3,0.96)), url(https://images.pexels.com/photos/374563/pexels-photo-374563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 p-10 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2 text-white">
            <Terminal className="w-5 h-5 text-[#22d3ee]" strokeWidth={1.5} />
            <span className="font-mono tracking-[0.2em] text-sm uppercase">
              armesa<span className="text-[#22d3ee]">.monitoring</span>
            </span>
          </div>
          <div className="font-mono text-[13px] text-[#8b8d98] leading-relaxed max-w-md">
            <div className="text-[#22d3ee] mb-2">$ ./login --secure</div>
            <div>
              <span className="text-[#eab308]">[INFO]</span> Control-plane für
              Prozesse, Services, Nginx & MongoDB.
            </div>
            <div>
              <span className="text-[#10b981]">[READY]</span> Warte auf
              Authentifizierung…
            </div>
            <div className="mt-4 text-[#3f3f46]">
              {" "}
              debian · systemd · nginx · mongodb
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — FORM */}
      <div className="flex items-center justify-center px-6 py-12">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm"
        >
          <div className="overline mb-3">// login required</div>
          <h1 className="text-4xl font-mono tracking-tighter uppercase font-medium text-white mb-2">
            Access.
          </h1>
          <p className="text-sm text-[#8b8d98] mb-8 max-w-sm">
            Nur autorisiertes Personal. Alle Aktionen werden protokolliert.
          </p>

          <label className="overline block mb-2">Benutzername</label>
          <input
            data-testid="login-username-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            className="w-full mb-5 bg-black border border-[#1f1f22] text-white font-mono px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#22d3ee] focus:border-[#22d3ee]"
            placeholder="armesa"
            autoComplete="username"
          />

          <label className="overline block mb-2">Passwort</label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-6 bg-black border border-[#1f1f22] text-white font-mono px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#22d3ee] focus:border-[#22d3ee]"
            placeholder="••••••••"
            autoComplete="current-password"
          />

          {err ? (
            <div
              data-testid="login-error"
              className="mb-4 flex items-start gap-2 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" strokeWidth={1.5} />
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 border border-[#22d3ee] bg-[#22d3ee]/10 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black transition-colors py-2.5 text-xs font-mono tracking-[0.2em] uppercase disabled:opacity-50"
          >
            <KeyRound className="w-4 h-4" strokeWidth={1.5} />
            {submitting ? "Authentifiziere…" : "Einloggen"}
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#1f1f22]" />
            <span className="overline">oder</span>
            <div className="h-px flex-1 bg-[#1f1f22]" />
          </div>

          <button
            type="button"
            data-testid="login-passkey-button"
            onClick={passkeyLogin}
            disabled={passkeyBusy}
            className="w-full flex items-center justify-center gap-2 border border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white transition-colors py-2.5 text-xs font-mono tracking-[0.2em] uppercase disabled:opacity-50"
          >
            <Fingerprint className="w-4 h-4" strokeWidth={1.5} />
            {passkeyBusy ? "Passkey…" : "Mit Passkey anmelden"}
          </button>

          <div className="mt-10 text-[10px] font-mono tracking-[0.2em] uppercase text-[#3f3f46]">
            &nbsp;monitoring.armesa.de · v1
          </div>
        </form>
      </div>
    </div>
  );
}
